import {makeAutoObservable} from "mobx";
import {api, catchApi,} from "../../api";
import {validationStore} from "../ValidationStore";
import {equals} from "../../utils";
import {SalutationTypeDto, UserDetailDto, UserGroupTypeDto} from "../../api/ums";
import {FormData} from "../../components/page/ums/UserForm";

class UserDetailStore {
    get initial(): UserDetailDto | undefined {
        return this._initial;
    }

    set initial(value: UserDetailDto | undefined) {
        this._initial = value;
    }

    get original(): UserDetailDto | undefined {
        return this._original;
    }

    set original(value: UserDetailDto | undefined) {
        this._original = value;
    }

    get id(): string | undefined {
        return this._id;
    }

    set id(value: string | undefined) {
        this._id = value;
    }

    private get group(): UserGroupTypeDto | null {
        return this._group;
    }

    private set group(value: UserGroupTypeDto | null) {
        this._group = value;
    }

    get salutation(): SalutationTypeDto | null {
        return this._salutation;
    }

    set salutation(value: SalutationTypeDto | null) {
        this._salutation = value;
    }

    get lastname(): string {
        return this._lastname;
    }

    set lastname(value: string) {
        this._lastname = value;
    }

    get firstname(): string {
        return this._firstname;
    }

    set firstname(value: string) {
        this._firstname = value;
    }

    get email(): string {
        return this._email;
    }

    set email(value: string) {
        this._email = value;
    }

    get remark(): string {
        return this._remark;
    }

    set remark(value: string) {
        this._remark = value;
    }

    get phoneNumber(): string {
        return this._phoneNumber;
    }

    set phoneNumber(value: string) {
        this._phoneNumber = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }

    get saving(): boolean {
        return this._saving;
    }

    set saving(value: boolean) {
        this._saving = value;
    }

    get dirty(): boolean {
        const current = this.buildDto()
        const dirty = !equals(this.initial, current)
        return dirty
    }

    private _group: UserGroupTypeDto | null = null
    private _email: string = ''
    private _firstname: string = ''
    private _lastname: string = ''
    private _salutation: SalutationTypeDto | null = null


    private _phoneNumber: string = ''
    private _remark: string = ''

    private _id: string | undefined
    private _saving: boolean = false
    private _valid: boolean = false
    private _original: UserDetailDto | undefined
    private _initial: UserDetailDto | undefined

    get formData(): FormData {
        return {
            username: this.original?.username,
            keycloakId: this.original?.keycloakId,
            group: this.group,
            salutation: this.salutation,
            lastname: this.lastname,
            firstname: this.firstname,
            email: this.email,
            phoneNumber: this.phoneNumber,
            remark: this.remark
        }
    }

    set formData({
                     group,
                     salutation,
                     lastname,
                     firstname,
                     email,
                     phoneNumber,
                     remark
                 }: FormData) {
        this.group = group || null
        this.salutation = salutation
        this.lastname = lastname
        this.firstname = firstname
        this.email = email
        this.phoneNumber = phoneNumber
        this.remark = remark
    }

    constructor() {
        makeAutoObservable(this)
    }

    async load(): Promise<UserDetailDto | undefined> {
        const {id} = this
        if (id) {
            const result = await catchApi(async () => {
                return await api.ums.api.getUser(id)
            })
            this.original = result?.data
            this.reset()
            return this.original
        }
    }

    async save() {
        const dto = this.buildDto()
        const id = this.id
        if (id) {
            const result = await catchApi(async () => {
                return await api.ums.api.updateUser(id, dto)
            })
            this.original = result?.data
            this.reset()
            return this.original
        }
    }

    async validate() {
        const dto = this.buildDto()
        const emailChanged =  this.original?.email !== this.email

        await validationStore.validateWithQuery(api.ums.api.validateUser, dto, {validateEmail: emailChanged})
        this.valid = Boolean(validationStore.valid)
    }

    reset() {
        const {original} = this
        if (!original) {
            return
        }
        const {
            group,
            email,
            lastname,
            salutation,
            firstname,
            phoneNumber,
            remark,
        } = original
        this.group = group
        this.email = email
        this.firstname = firstname
        this.lastname = lastname
        this.salutation = salutation || null
        this.phoneNumber = phoneNumber
        this.remark = remark || ''
        this.initial = this.buildDto()
        validationStore.reset()
    }

    private buildDto(): UserDetailDto {
        const {
            group,
            email,
            firstname,
            lastname,
            salutation,
            phoneNumber,
            remark,
        } = this
        return {
            active: true,
            group: group!!,
            email,
            firstname,
            lastname,
            salutation: salutation || undefined,
            phoneNumber,
            remark,
        }
    }
}

const userDetailStore = new UserDetailStore()

export {
    userDetailStore
}
